import type { RevenueAnalyticsRow } from './types';

/**
 * Fraction (0–1) of start-of-day inventory still remaining. Returns `null`
 * when the backend reports no inventory for the row (so the UI can render a
 * placeholder rather than a misleading 0), and 0 when SOD is present but zero.
 */
export function percentRemaining(row: Pick<RevenueAnalyticsRow, 'sodInventory' | 'remainingInventory'>): number | null {
    if (row.sodInventory === null) return null;
    if (row.sodInventory <= 0) return 0;
    // Clamp to [0, 1]: a row can occasionally report more remaining than SOD
    // after intraday adjustments, which would otherwise overflow the bar.
    return Math.min(1, Math.max(0, (row.remainingInventory ?? 0) / row.sodInventory));
}

/**
 * Fraction (0–1) of start-of-day inventory consumed. The complement of
 * {@link percentRemaining}, so the two always sum to 1 for a row with
 * inventory; `null` when the row has no inventory data.
 */
export function percentUsed(row: Pick<RevenueAnalyticsRow, 'sodInventory' | 'remainingInventory'>): number | null {
    if (row.sodInventory === null) return null;
    if (row.sodInventory <= 0) return 0;
    return 1 - (percentRemaining(row) ?? 0);
}
